import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, Button, Modal, Table, TableBody, TableContainer,
    TableHead, TableRow, Paper, TablePagination,
    Typography, CircularProgress, TextField,
    InputAdornment, Menu, MenuItem, IconButton, Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from 'react-toastify';
import Papa from "papaparse";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getReportForAdminByStrength } from '../../../services/reportServices/adminReportServices/AttendanceReportServices';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#7e7e7e",
        color: theme.palette.common.white,
        textTransform: "capitalize",
        fontSize: 16,
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd),&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

function highlightText(text, search) {
    if (!search || !text) return text;
    const parts = text.toString().split(new RegExp(`(${search})`, "gi"));
    return parts.map((part, index) =>
        part.toLowerCase() === search.toLowerCase() ? (
            <span key={index} style={{ backgroundColor: "yellow" }}>
                {part}
            </span>
        ) : (
            part
        )
    );
}

function AdminAttendanceModal({ onClose, open, selected, selectedAttendance }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [row, setRow] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);

    useEffect(() => {
        if (!open || (!selected && !selectedAttendance)) return;

        function convertDateFormat(dateStr) {
            if (!dateStr) return "";
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
            const months = {
                Jan: "01", Feb: "02", Mar: "03", Apr: "04",
                May: "05", Jun: "06", Jul: "07", Aug: "08",
                Sep: "09", Oct: "10", Nov: "11", Dec: "12",
            };
            const parts = dateStr.split("-");
            if (parts.length < 3) return dateStr;
            const [day, mon, year] = parts;
            const month = months[mon] || "01";
            return `${year}-${month}-${day.padStart(2, "0")}`;
        }

        const fetchData = async () => {
            try {
                setLoading(true);
                const coeId = selected?.coe || selected?.coeId || selected?.coe_id || selected?.coeID || selectedAttendance?.coeId || selectedAttendance?.coeID || selectedAttendance?.coe_id;
                const labType = selected?.labType || selected?.lab_type || selectedAttendance?.labType || selectedAttendance?.lab_type;
                const attendanceName = selectedAttendance?.attendanceName || selectedAttendance?.attendance_name || selected?.attendanceName || selected?.attendance_name;
                const rawDate = selectedAttendance?.AttendanceDate || selectedAttendance?.attendanceDate || selectedAttendance?.forDate || selectedAttendance?.date || selected?.AttendanceDate || selected?.attendanceDate || selected?.forDate;
                const timeSlot = selectedAttendance?.SessionTime || selectedAttendance?.sessionTime || selectedAttendance?.timeSlot || selected?.SessionTime || selected?.timeSlot;
                const labId = selectedAttendance?.lab_id || selectedAttendance?.labId || selected?.labId || selected?.lab_id;

                if (coeId && labType && attendanceName && rawDate) {
                    const forDate = convertDateFormat(rawDate);
                    const response = await getReportForAdminByStrength(
                        coeId,
                        labType,
                        timeSlot,
                        attendanceName,
                        forDate,
                        labId
                    );
                    const rawData = response?.data?.data || response?.data;
                    setRow(rawData);
                }
            } catch (error) {
                toast.error(error?.response?.data?.message || error?.message || "Error fetching COE data");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [open, selected, selectedAttendance]);

    const learners = useMemo(() => {
        if (!row) return [];
        if (Array.isArray(row)) {
            if (row.length > 0 && Array.isArray(row[0]?.learners)) {
                return row[0].learners;
            }
            return row;
        }
        if (Array.isArray(row?.learners)) return row.learners;
        if (Array.isArray(row?.data)) return row.data;
        return [];
    }, [row]);

    const filteredData = useMemo(() => {
        if (!learners || learners.length === 0) return [];
        if (!search) return learners;
        const value = search.toLowerCase();
        return learners.filter((item) => {
            const dateVal = String(item?.AttendanceDate || item?.attendanceDate || item?.date || selectedAttendance?.AttendanceDate || "").toLowerCase();
            const labVal = String(item?.lab_name || item?.labName || selectedAttendance?.lab_name || "").toLowerCase();
            const attNameVal = String(item?.attendanceName || item?.attendance_name || selectedAttendance?.attendanceName || "").toLowerCase();
            const rollVal = String(item?.roll_num || item?.rollNo || item?.StudentRollNo || item?.roll_number || "").toLowerCase();
            const nameVal = String(item?.learnerName || item?.name || item?.StudentName || item?.studentName || "").toLowerCase();
            const emailVal = String(item?.email || item?.StudentEmail || item?.studentEmail || "").toLowerCase();
            const statusVal = String(item?.attendanceStatus || item?.status || item?.attendance_status || "").toLowerCase();

            return (
                dateVal.includes(value) ||
                labVal.includes(value) ||
                attNameVal.includes(value) ||
                rollVal.includes(value) ||
                nameVal.includes(value) ||
                emailVal.includes(value) ||
                statusVal.includes(value)
            );
        });
    }, [learners, search, selectedAttendance]);

    const paginatedRows = useMemo(() => {
        return filteredData?.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
        ) || [];
    }, [filteredData, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDownloadClick = (event) => setAnchorEl(event.currentTarget);
    const handleCloseMenu = () => setAnchorEl(null);

    const handleDownload = (type) => {
        handleCloseMenu();

        if (!filteredData.length) {
            toast.warning("No data to export!");
            return;
        }

        const formatted = filteredData.map((r) => ({
            Date: r.AttendanceDate || r.attendanceDate || r.date || selectedAttendance?.AttendanceDate || "-",
            "Lab Name": r.lab_name || r.labName || selectedAttendance?.lab_name || "-",
            "Roll No": r.roll_num || r.rollNo || r.StudentRollNo || r.roll_number || "-",
            "Student Name": r.learnerName || r.name || r.StudentName || r.studentName || "-",
            "Student Email": r.email || r.StudentEmail || r.studentEmail || "-",
            Status: r.attendanceStatus || r.status || r.attendance_status || "-"
        }));

        if (type === "csv") {
            const csv = Papa.unparse(formatted);
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "Admin_Attendance_Strength_Report.csv";
            link.click();
        }

        if (type === "pdf") {
            const doc = new jsPDF();
            autoTable(doc, {
                startY: 20,
                head: [[
                    "Date", "Lab Name", "Roll No", "Name", "Email", "Status"
                ]],
                body: formatted.map((r) => Object.values(r)),
                theme: "grid",
                headStyles: { fillColor: [126, 126, 126] },
                styles: { fontSize: 9, halign: "center" },
                didDrawPage: () => {
                    doc.setFontSize(12);
                    doc.text("Max Attended Students Report", 14, 15);
                }
            });
            doc.save("Max_Attended_Students_Report.pdf");
        }
    };

    const modalTitle = "Max Attended Students";

    const labName = selectedAttendance?.lab_name || selectedAttendance?.labName || selected?.lab_name || selected?.labName || (row && row[0]?.lab_name) || "-";
    const coeName = selected?.coe_name || selectedAttendance?.coe_name || (row && row[0]?.coe_name) || "";
    const dateVal = selectedAttendance?.AttendanceDate || selectedAttendance?.attendanceDate || selectedAttendance?.date || selected?.AttendanceDate || (row && row[0]?.AttendanceDate) || "-";
    const attendanceName = selectedAttendance?.attendanceName || selectedAttendance?.attendance_name || selected?.attendanceName || (row && row[0]?.attendanceName) || "";
    const totalCount = Array.isArray(row) ? row.length : 0;

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                outline: "none !important",
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: "95%", sm: "90%", md: "85%" },
                maxWidth: '95vw',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
                maxHeight: "90vh !important",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",}}>
                <Box display="flex" justifyContent="space-between" alignItems="center" pb={2}>
                    <Box>
                        <Typography
                            className="dashboard-header-title"
                            sx={{ whiteSpace: "nowrap" }}
                        >
                            {modalTitle}
                        </Typography>

                        {!loading && (
                            <Typography variant="body2" color="text.secondary" mt={0.5}>
                                Lab: <strong>{labName}</strong>
                                {coeName ? <>&nbsp;|&nbsp; COE: <strong>{coeName}</strong></> : null}
                                {dateVal && dateVal !== "-" ? <>&nbsp;|&nbsp; Date: <strong>{dateVal}</strong></> : null}
                                {attendanceName && attendanceName !== "-" ? <>&nbsp;|&nbsp; Attendance: <strong>{attendanceName}</strong></> : null}
                                &nbsp;|&nbsp; Total: <strong>{totalCount}</strong>
                            </Typography>
                        )}
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                        <TextField
                            size="small"
                            placeholder="Search by name, roll no, email..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            sx={{ minWidth: "280px" }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Box>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadClick}
                            >
                                Download
                            </Button>
                            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                                <MenuItem onClick={() => handleDownload("csv")}>Download CSV</MenuItem>
                                <MenuItem onClick={() => handleDownload("pdf")}>Download PDF</MenuItem>
                            </Menu>
                        </Box>
                        <Tooltip title="Close">
                            <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer
                            component={Paper}
                            sx={{
                                maxHeight: "calc(90vh - 150px)",
                                overflow: "auto",
                            }}
                        >
                            <Table stickyHeader sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }}>
                                <TableHead>
                                    <TableRow>
                                        {["Date", "Lab Name", "Roll No", "Student Name", "Student Email", "Status"]
                                            .map((header) => (
                                                <StyledTableCell key={header}>{header}</StyledTableCell>
                                            ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {paginatedRows.length ? paginatedRows.map((item, idx) => (
                                        <StyledTableRow key={idx}>
                                            <StyledTableCell>
                                                {highlightText(item.AttendanceDate || item.attendanceDate || item.date || selectedAttendance?.AttendanceDate || "-", search)}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {highlightText(item.lab_name || item.labName || selectedAttendance?.lab_name || "-", search)}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {highlightText(item.roll_num || item.rollNo || item.StudentRollNo || item.roll_number || "-", search)}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {highlightText(item.learnerName || item.name || item.StudentName || item.studentName || "-", search)}
                                            </StyledTableCell>
                                            <StyledTableCell sx={{ textTransform: "none" }}>
                                                {highlightText(item.email || item.StudentEmail || item.studentEmail || "-", search)}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {highlightText(item.attendanceStatus || item.status || item.attendance_status || "-", search)}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )) : (
                                        <TableRow>
                                            <StyledTableCell colSpan={6} align="center">
                                                No records found.
                                            </StyledTableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[10, 20, 30, 60, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </>
                )}
            </Box>
        </Modal>
    );
}

export default AdminAttendanceModal;
